import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SubdomainSection from '../components/SubdomainSection';
import PresetForm from '../components/PresetForm';
import PresetCard from '../components/PresetCard';

function Dashboard({ user, onLogout, onUpdateUser }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const response = await axios.get('/api/presets');
      setPresets(response.data);
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePreset = async (presetData) => {
    try {
      const response = await axios.post('/api/presets', presetData);
      
      // If the new preset is active, mark all others as inactive
      if (response.data.is_active) {
        setPresets([response.data, ...presets.map(p => ({ ...p, is_active: false }))]);
      } else {
        setPresets([response.data, ...presets]);
      }
      
      setShowForm(false);
    } catch (error) {
      console.error('Failed to create preset:', error);
      throw error;
    }
  };

  const handleUpdatePreset = async (id, presetData) => {
    try {
      const response = await axios.put(`/api/presets/${id}`, presetData);
      
      // If updating to active, mark all others as inactive
      if (response.data.is_active) {
        setPresets(presets.map(p => 
          p.id === id ? response.data : { ...p, is_active: false }
        ));
      } else {
        setPresets(presets.map(p => p.id === id ? response.data : p));
      }
      
      setEditingPreset(null);
    } catch (error) {
      console.error('Failed to update preset:', error);
      throw error;
    }
  };

  const handleDeletePreset = async (id) => {
    if (!window.confirm('Are you sure you want to delete this preset?')) {
      return;
    }

    try {
      await axios.delete(`/api/presets/${id}`);
      setPresets(presets.filter(p => p.id !== id));
    } catch (error) {
      console.error('Failed to delete preset:', error);
    }
  };

  const handleActivatePreset = async (id) => {
    try {
      const response = await axios.post(`/api/presets/${id}/activate`);
      setPresets(presets.map(p => ({
        ...p,
        is_active: p.id === id
      })));
    } catch (error) {
      console.error('Failed to activate preset:', error);
    }
  };

  const handleEditPreset = (preset) => {
    setEditingPreset(preset);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingPreset(null);
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Stranger</h1>
        <div className="header-actions">
          <span className="user-email">{user?.email}</span>
          <button className="btn-secondary" onClick={onLogout}>
            Sign Out
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <SubdomainSection user={user} onUpdateUser={onUpdateUser} />

        <div className="presets-section">
          <div className="section-header">
            <h2 className="section-title">Contact Presets</h2>
            {!showForm && (
              <button 
                className="btn-primary" 
                style={{ width: 'auto', padding: '0.75rem 1.5rem' }}
                onClick={() => setShowForm(true)}
              >
                + New Preset
              </button>
            )}
          </div>

          {showForm && (
            <PresetForm
              preset={editingPreset}
              onSave={editingPreset ? 
                (data) => handleUpdatePreset(editingPreset.id, data) : 
                handleCreatePreset
              }
              onCancel={handleCancelForm}
            />
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
            </div>
          ) : presets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📇</div>
              <div className="empty-state-text">No contact presets yet</div>
              <div className="empty-state-subtext">
                Create your first preset to start sharing your contact information
              </div>
            </div>
          ) : (
            <div className="presets-grid">
              {presets.map(preset => (
                <PresetCard
                  key={preset.id}
                  preset={preset}
                  onActivate={handleActivatePreset}
                  onEdit={handleEditPreset}
                  onDelete={handleDeletePreset}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
